// hooks/document/documentsApi.js

/**
 * API client for document management endpoints
 * Uses centralized apiService for consistency
 */

import apiService from '../../services/api';

/**
 * Documents API client
 */
export const documentsApi = {
  /**
   * Upload a document
   */
  uploadDocument: async (file, displayName, category) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('displayName', displayName);
    formData.append('category', category);

    const token = apiService.getUserToken();

    const response = await fetch(`${apiService.getBaseURL()}/documents/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
      credentials: 'omit',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * List documents with optional filters
   */
  listDocuments: async ({ category, search, limit = 50, offset = 0 } = {}) => {
    return apiService.get('/documents', {
      category,
      search,
      limit,
      offset,
    });
  },

  /**
   * Get preview URL for a document
   */
  getPreviewUrl: async (documentId) => {
    return apiService.get(`/documents/${documentId}/preview`);
  },

  /**
   * Get download URL for a document
   */
  getDownloadUrl: async (documentId) => {
    return apiService.get(`/documents/${documentId}/download`);
  },

  /**
   * Update document metadata
   */
  updateDocument: async (documentId, displayName, category, tags, bidStatus, engagementType, engagementId) => {
    const updates = {};
    if (displayName !== undefined)    updates.display_name = displayName;
    if (category !== undefined)       updates.category = category;
    if (tags !== undefined)           updates.tags = tags;
    if (bidStatus !== undefined)      updates.bid_status = bidStatus;
    if (engagementType !== undefined) updates.engagement_type = engagementType;
    if (engagementId !== undefined)   updates.engagement_id = engagementId; // ← ADD

    return apiService.patch(`/documents/${documentId}`, updates);
  },

  /**
   * Delete a document
   */
  deleteDocument: async (documentId) => {
    return apiService.delete(`/documents/${documentId}`);
  },

  /**
   * Extract structured data from a proposal PDF using Gemini
   * Optionally links result to an engagement
   */
  extractProposal: async (documentId, engagementId = null) => {
    const url = engagementId
      ? `/document-ai/${documentId}/extract?engagement_id=${engagementId}`
      : `/document-ai/${documentId}/extract`;
    return apiService.post(url, {});
  },


  /**
   * Get the stored extraction result for a document
   */
  getExtractionResult: async (documentId) => {
    return apiService.get(`/document-ai/${documentId}/extraction`);
  },
};

export default documentsApi;